import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { RotateCcw } from "lucide-react";

// =============================================================================
// SignaturePad — vector signature capture
// =============================================================================
// Captures ink strokes as SVG paths rather than a raster PNG. Advantages:
//   - Tiny payload (a few KB)
//   - Renders crisply at any resolution
//   - Stroke-level data means we can later detect "someone just tapped"
//     style fake signatures by checking stroke count / duration
//
// Public API via the imperative ref:
//   .getSvg()      → the full SVG string (or null if empty)
//   .isEmpty()     → boolean
//   .clear()       → wipe
//
// The svg output is safe to stick in a Postgres `text` column.
// =============================================================================

interface Point { x: number; y: number }
type Stroke = Point[];

export interface SignaturePadHandle {
  getSvg(): string | null;
  isEmpty(): boolean;
  clear(): void;
  /** Stroke-level metadata (for anti-fraud heuristics on the server) */
  getMeta(): { strokeCount: number; totalPoints: number; durationMs: number };
}

interface Props {
  width?: number;
  height?: number;
  /** Called when the first stroke starts so the parent can enable a Save button. */
  onFirstStroke?: () => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { width = 500, height = 180, onFirstStroke },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const currentRef = useRef<Stroke | null>(null);
  const drawingRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const [empty, setEmpty] = useState(true);

  // Cached canvas geometry for coordinate conversion
  const rectRef = useRef<DOMRect | null>(null);

  // -----------------------------------------------------------------
  // Drawing
  // -----------------------------------------------------------------
  function ctx() { return canvasRef.current?.getContext("2d") ?? null; }

  function renderStroke(s: Stroke) {
    const c = ctx(); if (!c || s.length < 2) return;
    c.beginPath();
    c.moveTo(s[0].x, s[0].y);
    for (let i = 1; i < s.length; i++) {
      const mid = { x: (s[i - 1].x + s[i].x) / 2, y: (s[i - 1].y + s[i].y) / 2 };
      c.quadraticCurveTo(s[i - 1].x, s[i - 1].y, mid.x, mid.y);
    }
    c.stroke();
  }

  function redrawAll() {
    const c = ctx(); if (!c || !canvasRef.current) return;
    c.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    strokesRef.current.forEach(renderStroke);
  }

  function ptFromEvent(e: PointerEvent | React.PointerEvent): Point {
    const rect = rectRef.current ?? canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    rectRef.current = canvasRef.current!.getBoundingClientRect();
    drawingRef.current = true;
    currentRef.current = [ptFromEvent(e)];
    if (startedAtRef.current == null) startedAtRef.current = Date.now();
    if (empty) { setEmpty(false); onFirstStroke?.(); }
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || !currentRef.current) return;
    currentRef.current.push(ptFromEvent(e));
    renderStroke(currentRef.current.slice(-2));     // only the new segment
  }

  function onPointerUp() {
    if (currentRef.current && currentRef.current.length > 1) {
      strokesRef.current.push(currentRef.current);
    }
    currentRef.current = null;
    drawingRef.current = false;
  }

  // -----------------------------------------------------------------
  // Size & pixel-ratio (so signatures stay crisp on Retina)
  // -----------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width  = width  * dpr;
    canvas.height = height * dpr;
    canvas.style.width  = `${width}px`;
    canvas.style.height = `${height}px`;
    const c = canvas.getContext("2d"); if (!c) return;
    c.scale(dpr, dpr);
    c.lineWidth = 2.2;
    c.lineCap = "round";
    c.lineJoin = "round";
    c.strokeStyle = "#F0EFEA";
    redrawAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  // -----------------------------------------------------------------
  // Imperative API
  // -----------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    isEmpty: () => strokesRef.current.length === 0,
    clear: () => {
      strokesRef.current = [];
      startedAtRef.current = null;
      setEmpty(true);
      redrawAll();
    },
    getSvg: () => {
      if (strokesRef.current.length === 0) return null;
      const paths = strokesRef.current.map(s => {
        if (s.length < 2) return "";
        let d = `M${s[0].x.toFixed(1)},${s[0].y.toFixed(1)}`;
        for (let i = 1; i < s.length; i++) {
          const mid = { x: (s[i - 1].x + s[i].x) / 2, y: (s[i - 1].y + s[i].y) / 2 };
          d += ` Q${s[i - 1].x.toFixed(1)},${s[i - 1].y.toFixed(1)} ${mid.x.toFixed(1)},${mid.y.toFixed(1)}`;
        }
        return `<path d="${d}" fill="none" stroke="#111" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`;
      }).join("");
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${paths}</svg>`;
    },
    getMeta: () => ({
      strokeCount: strokesRef.current.length,
      totalPoints: strokesRef.current.reduce((a, s) => a + s.length, 0),
      durationMs: startedAtRef.current ? Date.now() - startedAtRef.current : 0,
    }),
  }));

  return (
    <div className="relative">
      <div
        className="relative rounded-md overflow-hidden"
        style={{ background: "#1A1A1E", border: "1px solid #2A2A30" }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: "none", display: "block", width: "100%", height, cursor: "crosshair" }}
        />
        {/* Sign-here guide line */}
        <div
          aria-hidden
          className="absolute left-6 right-6 border-t border-dashed pointer-events-none"
          style={{ bottom: 28, borderColor: empty ? "#3A3A42" : "transparent" }}
        />
        {empty && (
          <div className="absolute inset-0 flex items-end justify-center pb-5 pointer-events-none text-text-muted text-[11px] uppercase tracking-wider2 font-mono">
            Sign here
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[11px] font-mono text-text-muted">
        <span>{empty ? "Ready" : "Captured"}</span>
        <button
          type="button"
          onClick={() => {
            strokesRef.current = []; startedAtRef.current = null;
            setEmpty(true); redrawAll();
          }}
          className="inline-flex items-center gap-1 text-text-muted hover:text-text"
        >
          <RotateCcw size={10} /> Clear
        </button>
      </div>
    </div>
  );
});
